<template lang="pug">
yz-filter-menu(:label="$t('panel.diagnosis')" :is-active="isActive" :display-value="displayValue" :disable-apply="selectedDiagnoses.length === 0" :loading="loading" :max-height="500" :show-header="false" @update:menu="onMenuOpen" @clear="onClear" @apply="onApply")
  .menu-header.sticky.top-0.z-1
    .font-semibold.text-sm.pa-4 {{ $t('filter_by') }} {{ $t('panel.diagnosis').toLowerCase() }}
    yz-text-field.text-sm.mx-4.mb-1(v-model="searchText" :placeholder="$t('search')" size="lg" @input="onQueryChanged")
    .d-flex.align-center.justify-end.px-4.py-2(v-if="count > pageSize")
      yz-pagination(v-model="page" :page-size="pageSize" :count="count" :loading="loading" arrow, type="item")

    yz-divider

  .scrollable-content
    yz-skeleton-loader.pa-4(v-if="loading" type="text@10")

    div(v-else-if="error")
      .text-center.py-8
        .text-xs.neutral500--text.mb-3 {{ $t('error_message.error_occured') }}
        yz-btn(size="sm" variant="outlined" color="primary" @click="fetchDiagnoses") {{ $t('retry') }}

    div(v-else)
      template(v-if="filteredDiagnoses.length > 0")
        div(v-for="(diagnosis, index) in filteredDiagnoses" :key="diagnosis.id")
          .d-flex.text-sm.px-4.py-3.cursor-pointer.hover-bg(@click="onToggleDiagnosis(diagnosis.id)")
            yz-checkbox(:value="selectedDiagnoses.includes(diagnosis.id)" @click.native.prevent)
            .ml-4.truncate {{ diagnosis.value }}
          yz-divider.my-0(v-if="index < filteredDiagnoses.length - 1")
      template(v-else)
        .text-center.py-8.text-xs.neutral500--text {{ $t('panel.no_items_found') }}
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters } from 'vuex'

export default {
  props: {
    filter: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      diagnoses: [],
      selectedDiagnoses: [],
      searchText: '',
      loading: false,
      isActive: false,
      displayValue: '',
      diagnosesLoaded: false,
      error: false,
      page: 1,
      pageSize: 10,
      count: 0
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    filteredDiagnoses() {
      return this.diagnoses
    }
  },
  watch: {
    filter: {
      handler() {
        if (!this.filter.key) {
          this.selectedDiagnoses = []
          this.isActive = false
          this.displayValue = ''
        }
      }
    },
    page() {
      this.fetchDiagnoses()
    }
  },
  methods: {
    onMenuOpen(isOpen) {
      if (isOpen && !this.diagnosesLoaded) {
        this.fetchDiagnoses()
      }
    },
    onQueryChanged: debounce(function() {
      this.page = 1
      this.fetchDiagnoses()
    }, 750),
    async fetchDiagnoses() {
      this.loading = true
      this.error = false
      try {
        const params = {
          company: this.company.id,
          enabled: true,
          field: 'consultation.diagnosis',
          search: this.searchText || null,
          page: this.page,
          page_size: this.pageSize
        }
        const response = await this.$axios.get('/options', { params })
        this.diagnoses = response.data.results || response.data || []
        this.count = response.data.count || 0
        this.diagnosesLoaded = true
      } catch (error) {
        console.error('Error fetching diagnoses:', error)
        this.diagnoses = []
        this.error = true
      } finally {
        this.loading = false
      }
    },
    onToggleDiagnosis(diagnosisId) {
      const index = this.selectedDiagnoses.indexOf(diagnosisId)
      if (index > -1) {
        this.selectedDiagnoses.splice(index, 1)
      } else {
        this.selectedDiagnoses.push(diagnosisId)
      }
    },
    onClear() {
      this.selectedDiagnoses = []
      this.searchText = ''
      this.isActive = false
      this.displayValue = ''
      this.$emit('update:filter', {})
    },
    onApply() {
      this.isActive = true
      const count = this.selectedDiagnoses.length

      if (count <= 2) {
        const labels = this.selectedDiagnoses.map(id => this.diagnoses.find(d => d.id === id)?.value)
        this.displayValue = labels.join(', ')
      } else {
        this.displayValue = `${count} ${this.$t('selected')}`
      }

      this.$emit('update:filter', {
        key: 'diagnosis',
        value: this.selectedDiagnoses.join(',')
      })
    },
    reset() {
      this.onClear()
    },
    async restoreFromParams(params) {
      // Restore filter state from URL params
      if (params.diagnosis) {
        // Make sure diagnoses are loaded first
        if (!this.diagnosesLoaded) {
          await this.fetchDiagnoses()
        }
        // Parse comma-separated IDs
        this.selectedDiagnoses = params.diagnosis.split(',').map(id => parseInt(id))
        this.onApply()
      }
    }
  }
}
</script>

<style scoped>
.hover-bg:hover {
  background-color: #f5f5f5;
}

.menu-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  overflow: hidden;
}

.scrollable-content {
  max-height: 300px;
  overflow-y: auto;
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #b0b0b0;
}
</style>
